import React, { useState, useEffect } from 'react'
import { Modal, ModalBody, ModalHeader, Box } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { useSetup } from 'contexts/SetupContext'
import { getSetupInProgressKey, STEPS, TOTAL_SETUP_STEPS } from 'utils/setupStorage'
import { RootState } from 'constants/interfaces'
import { SmartAvatar } from 'components/CommonComponents/SmartAvatar'
import useAvatar from 'hooks/useAvatar'
import colors from 'constants/colors'
import { useUserName } from 'hooks/useUserName'
import useLocaleText from 'components/useLocaleText'

import { DEFAULT_KPIS, EXTERNAL_LINK, KPIItem, AIFeedOption, DataSourceOption } from './constants'
import { StyledModalOverlay, StyledModalContent, ModalContainer, UserName, ActivateButton } from './styles'
import { FeedStep, AIFeedStep, ReportsStep, KPIsStep } from './steps'

interface SetupModalProps {
  stepId: number
  stepTitle: string
  isOpen: boolean
  onClose: () => void
}

export const SetupModal: React.FC<SetupModalProps> = ({ stepId, isOpen, onClose }) => {
  const { completeStep } = useSetup()
  const uid = useSelector((state: RootState) => state.auth.uid)

  // FEED step state
  const [inputValue, setInputValue] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(false)

  // KPI state for RANKINGS_LIST step
  const [kpiItems, setKpiItems] = useState<KPIItem[]>(DEFAULT_KPIS)
  const [newKpiValue, setNewKpiValue] = useState('')
  const [isAddingKpi, setIsAddingKpi] = useState(false)

  // REPORTS step - data source selection
  const [selectedDataSource, setSelectedDataSource] = useState<DataSourceOption>('demo')

  // AI_FEED step - personalization option selection
  const [selectedAIFeedOption, setSelectedAIFeedOption] = useState<AIFeedOption>('custom')
  const [customInstruction, setCustomInstruction] = useState('')

  const avatar = useAvatar(uid!)
  const userName = useUserName(uid!)
  const t_activate = useLocaleText('activate', true)

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('')
      setIsEmailValid(false)
      setKpiItems(DEFAULT_KPIS)
      setNewKpiValue('')
      setIsAddingKpi(false)
      setSelectedDataSource(null)
      setSelectedAIFeedOption('custom')
      setCustomInstruction('')
    }
  }, [isOpen])

  const handleActivate = () => {
    if (!uid) return

    // Handle REPORTS step with 'connect' option - redirect to external link
    if (stepId === STEPS.REPORTS && selectedDataSource === 'connect') {
      window.open(EXTERNAL_LINK, '_blank')
      setSelectedDataSource(null)
      return
    }

    onClose()
    setTimeout(() => {
      completeStep(stepId)
    }, 1000)

    // Handle final step completion
    if (stepId === TOTAL_SETUP_STEPS) {
      localStorage.removeItem(getSetupInProgressKey(uid))
      return
    }
  }

  const handleOverlayClick = () => {
    // Dispatch custom event to notify that modal was closed by overlay click
    window.dispatchEvent(new CustomEvent('setupModalClosedByOverlay'))
    onClose()
  }

  const isActivateDisabled = (): boolean => {
    switch (stepId) {
      case STEPS.FEED:
        return !isEmailValid
      case STEPS.AI_FEED:
        if (!selectedAIFeedOption) return true
        if (selectedAIFeedOption === 'custom') return !customInstruction.trim()
        return false
      case STEPS.REPORTS:
        return !selectedDataSource
      case STEPS.RANKINGS_LIST:
        return kpiItems.every((kpi) => !kpi.isChecked)
      default:
        return false
    }
  }

  const renderStepContent = () => {
    switch (stepId) {
      case STEPS.FEED:
        return <FeedStep inputValue={inputValue} setInputValue={setInputValue} onValidationChange={setIsEmailValid} />
      case STEPS.AI_FEED:
        return (
          <AIFeedStep
            selectedOption={selectedAIFeedOption}
            setSelectedOption={setSelectedAIFeedOption}
            customInstruction={customInstruction}
            setCustomInstruction={setCustomInstruction}
            isOpen={isOpen}
          />
        )
      case STEPS.REPORTS:
        return <ReportsStep selectedDataSource={selectedDataSource} setSelectedDataSource={setSelectedDataSource} />
      case STEPS.RANKINGS_LIST:
        return (
          <KPIsStep
            kpiItems={kpiItems}
            setKpiItems={setKpiItems}
            newKpiValue={newKpiValue}
            setNewKpiValue={setNewKpiValue}
            isAddingKpi={isAddingKpi}
            setIsAddingKpi={setIsAddingKpi}
          />
        )
      default:
        return null
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleOverlayClick} isCentered size="md" closeOnOverlayClick={true}>
      <StyledModalOverlay onClick={handleOverlayClick} />
      <StyledModalContent maxWidth={stepId === 3 ? '42rem' : '32rem'}>
        <ModalHeader pos="relative">
          <Box pos="absolute" top="0" left="50%" transform="translate(-50%, -50%)">
            <SmartAvatar image={avatar} size="140px" backgroundColor={colors.greyLight4} iconColor={colors.tealLight} />
          </Box>
        </ModalHeader>
        <ModalBody>
          <ModalContainer>
            <UserName>{userName}</UserName>
            {renderStepContent()}
            <ActivateButton isDisabled={isActivateDisabled()} onClick={handleActivate}>
              {t_activate}
            </ActivateButton>
          </ModalContainer>
        </ModalBody>
      </StyledModalContent>
    </Modal>
  )
}
